/**
 * Home component
 *
 * Renders the main home page layout, including a header with the user's name,
 * breadcrumb navigation, and a subtitle. Below the header, it displays the
 * main action cards section.
 *
 * Context is used to retrieve user data for personalization.
 */

import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Heading, Text, ThemeStore, toast } from "@foris/avocado-suite";
import { Redirect, useLocation } from "react-router-dom";
import { ContextApp } from "@config/Context/contextApp";
import { Header } from "@common/components";
import useGetModulesUrls from "@common/hooks/useGetModulesUrls";
import useContextUrl from "@common/hooks/useContextUrl";
import ActionCards from "@modules/home/components/action-cards";
import Layout from "@common/layout/Layout";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import useBookingAccess from "@common/hooks/useBookingAccess";
import TourTooltipBeacon from "@common/components/tour-tooltip-beacon";
import {
  SCHEDULER_EDITOR_BOX_ONBOARDING,
  setIsOnboardingOpen,
  shouldShowHotspot,
  useOnboardingStore,
} from "@context/useOnboardingStore";
import OnboardingVideoModal from "@common/components/onboarding-video-modal";
import TecPastSessionsOnboarding from "@modules/home/components/tec-past-sessions-onboarding";
import css from "./home.module.scss";

const Home: FC = () => {
  // Hooks for translation and global user context
  const { t } = useTranslation();
  const { user } = useContext(ContextApp);
  const { workspace, scenario, origin } = useContextUrl();
  const { baseRoutes } = useNavigationUrl();
  const { isBaseDataLoaded, hasSomeModulesAccesibles } = useGetModulesUrls();
  const { isBookingAccesible } = useBookingAccess();
  const location = useLocation();
  const themeMode = ThemeStore.useThemeStore(state => state.theme.mode);
  const onboardingsSeen = useOnboardingStore(state => state.onboardingsSeen);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const showHotspot = useMemo(() => {
    return shouldShowHotspot(SCHEDULER_EDITOR_BOX_ONBOARDING);
  }, [onboardingsSeen]);

  const redirectUrl = useMemo(() => {
    const {
      workspaceId: userWorkspaceId,
      scenarioId: userScenarioId,
      originId: userOriginId,
    } = user?.preferences?.cubeContext ?? user?.context ?? {};

    const workspaceId = workspace ?? userWorkspaceId ?? null;
    const scenarioId = scenario ?? userScenarioId ?? null;
    const originId = origin ?? userOriginId ?? null;

    const contextUrl = `${workspaceId}/${scenarioId}${originId ? `/${originId}` : ""}`;

    if (!hasSomeModulesAccesibles && isBaseDataLoaded && isBookingAccesible) {
      return `${baseRoutes.bookingList}${contextUrl}`;
    }

    return `${baseRoutes.home}${contextUrl}`;
  }, [
    hasSomeModulesAccesibles,
    isBaseDataLoaded,
    isBookingAccesible,
    baseRoutes,
    workspace,
    scenario,
    origin,
    user,
  ]);

  if (!workspace && !scenario && !origin && (!!user.context || !!user?.preferences?.cubeContext)) {
    const { workspaceId, scenarioId } = user?.preferences?.cubeContext ?? user?.context ?? {};

    if (workspaceId && scenarioId) {
      return <Redirect to={redirectUrl} />;
    }
  }

  if (
    redirectUrl &&
    !hasSomeModulesAccesibles &&
    isBaseDataLoaded &&
    isBookingAccesible &&
    !!workspace &&
    !!scenario
  ) {
    return <Redirect to={redirectUrl} />;
  }

  useEffect(() => {
    if (location?.state && (location?.state as any)?.sectionDelete) {
      window.history.replaceState({}, document.title);

      toast.success(t("dimension.section.delete-section.success-message"), {
        autoClose: 5000,
      });
    }
  }, []);

  return (
    <Layout contextSearch={false}>
      <TecPastSessionsOnboarding userId={user?.id} />

      {showHotspot && (
        <OnboardingVideoModal
          isOpen={showVideoModal}
          title={t("onboarding.scheduler-editor-box.title")}
          description={t("onboarding.scheduler-editor-box.description")}
          videoUrl="https://files.foris.ai/darwin/onboarding/Nuevo+Home+-+PROD.mp4"
          onClose={() => {
            setShowVideoModal(false);
            setIsOnboardingOpen(false, SCHEDULER_EDITOR_BOX_ONBOARDING);
          }}
        />
      )}

      <Header
        className={css.header}
        title={
          <div className={css.header_title}>
            <Heading type="h1" className={css.titleHeading}>{`${t("home.title")} ${
              user?.name
            }`}</Heading>

            {showHotspot && (
              <div
                className={css.tourTooltipBeacon}
                onClick={() => {
                  setShowVideoModal(true);
                  setIsOnboardingOpen(true, SCHEDULER_EDITOR_BOX_ONBOARDING);
                }}
              >
                <TourTooltipBeacon mode={themeMode} />
              </div>
            )}
          </div>
        }
        breadcrumbItems={[
          {
            title: t("home.breadcrumb.current"),
          },
        ]}
        subTitle={<Text darkMode>{t("home.subtitle")}</Text>}
      />
      <main className={css.home}>
        <ActionCards />
      </main>
    </Layout>
  );
};

export default Home;
